import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutGrid, Calendar, Settings, LogOut, Users, CalendarDays, Trophy, Shield, MoreHorizontal, X, Bell } from 'lucide-react';
import { Button } from './ui/button';

const isCoachRole = (role) => role === 'coach' || role === 'head_coach';

const getRoleLabel = (role) => {
  if (role === 'head_coach') return 'Head Coach';
  return role.charAt(0).toUpperCase() + role.slice(1);
};

export default function DashboardLayout({ user, onLogout }) {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const allNavItems = [
    { name: 'Whiteboard', path: '/whiteboard', icon: LayoutGrid, primary: true },
    { name: 'Schedule', path: '/schedule', icon: Calendar, primary: true },
    { name: 'WOD Calendar', path: '/wod-calendar', icon: CalendarDays, primary: user.role === 'athlete' },
    { name: 'Leaderboard', path: '/leaderboard', icon: Trophy, primary: false },
  ];

  if (isCoachRole(user.role)) {
    allNavItems.push({ name: 'Coach Dashboard', path: '/coach-dashboard', icon: Settings, primary: true });
  }

  if (user.role === 'head_coach') {
    allNavItems.push({ name: 'Membership', path: '/membership', icon: Shield, primary: false });
  }

  if (user.role === 'athlete') {
    allNavItems.push({ name: 'My Stats', path: '/athlete-dashboard', icon: Users, primary: false });
  }

  allNavItems.push({ name: 'Notifications', path: '/notifications', icon: Bell, primary: false });

  const primaryNav = allNavItems.filter(i => i.primary);
  const moreNav = allNavItems.filter(i => !i.primary);
  const isMoreActive = moreNav.some(i => location.pathname === i.path);

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col md:flex-row">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-[#09090B] border-r border-[#27272A]" data-testid="desktop-sidebar">
        <div className="p-6 border-b border-[#27272A]">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_wod-trainer/artifacts/rhui1jxy_R%26R%20ELITE_20250812_172035_0000.png"
              alt="R&R Elite Fitness"
              className="w-12 h-12 object-contain rounded-sm"
            />
            <div>
              <h1 className="text-2xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA]">
                R&R Elite Fitness
              </h1>
              <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">{getRoleLabel(user.role)}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2" data-testid="desktop-nav">
          {allNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (location.pathname === '/' && item.path === '/whiteboard');
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                  isActive
                    ? 'bg-[#CCFF00] text-black font-bold shadow-[0_0_15px_rgba(204,255,0,0.2)]'
                    : 'text-[#A1A1AA] hover:bg-[#18181B] hover:text-[#FAFAFA]'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-semibold uppercase tracking-wide text-sm">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#27272A]">
          <div className="mb-4 p-3 bg-[#18181B] rounded-sm border border-[#27272A]">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wide mb-1">Logged in as</p>
            <p className="text-sm text-[#FAFAFA] font-semibold truncate">{user.name}</p>
            <p className="text-xs text-[#A1A1AA] truncate">{user.email}</p>
          </div>
          <Button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full bg-transparent border border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00] uppercase tracking-wider transition-all rounded-sm"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 pb-20 md:pb-0" data-testid="main-content">
        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 z-40 bg-[#09090B]/95 backdrop-blur-lg border-b border-[#27272A] px-4 py-3">
          <div className="flex items-center justify-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_wod-trainer/artifacts/rhui1jxy_R%26R%20ELITE_20250812_172035_0000.png"
              alt="R&R Elite Fitness"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-lg font-heading font-black uppercase tracking-tighter text-[#FAFAFA]">
                R&R Elite Fitness
              </h1>
              <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">{getRoleLabel(user.role)}</p>
            </div>
          </div>
        </div>
        <Outlet />
      </main>

      {/* Mobile "More" Menu Overlay */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="more-menu-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMoreOpen(false)} />
          <div className="absolute bottom-20 left-3 right-3 bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden shadow-2xl shadow-black/50">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A]">
              <span className="text-xs text-[#A1A1AA] uppercase tracking-wide font-semibold">More</span>
              <button onClick={() => setMoreOpen(false)} className="text-[#A1A1AA]">
                <X className="w-4 h-4" />
              </button>
            </div>
            {moreNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`more-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => setMoreOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3.5 transition-colors ${
                    isActive
                      ? 'bg-[#CCFF00]/10 text-[#CCFF00]'
                      : 'text-[#FAFAFA] active:bg-[#27272A]'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold uppercase tracking-wide text-sm">{item.name}</span>
                </Link>
              );
            })}
            <button
              onClick={() => { setMoreOpen(false); onLogout(); }}
              data-testid="more-logout-button"
              className="flex items-center gap-3 px-4 py-3.5 w-full text-[#A1A1AA] active:bg-[#27272A] border-t border-[#27272A]"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-semibold uppercase tracking-wide text-sm">Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#09090B] border-t border-[#27272A] z-50" data-testid="mobile-nav">
        <div className="flex items-center h-16">
          {primaryNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (location.pathname === '/' && item.path === '/whiteboard');
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMoreOpen(false)}
                data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  isActive ? 'text-[#CCFF00]' : 'text-[#71717A]'
                }`}
              >
                <Icon className="w-5 h-5 mb-0.5" />
                <span className="text-[10px] font-semibold uppercase tracking-wide">
                  {item.name.split(' ')[0]}
                </span>
              </Link>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            data-testid="mobile-more-button"
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              moreOpen || isMoreActive ? 'text-[#CCFF00]' : 'text-[#71717A]'
            }`}
          >
            <MoreHorizontal className="w-5 h-5 mb-0.5" />
            <span className="text-[10px] font-semibold uppercase tracking-wide">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
